import axios from "axios"
import React, {useEffect} from "react"
import {cljjs} from "dgetz/tsclj"
import {QueryClient, QueryClientProvider, useQuery, useMutation} from "@tanstack/react-query"
import {scrollToIdSmooth} from 'ladder/browser2'
import {inchesLbsToBmi} from 'ladder/common2'
import {canonicalHeaders} from 'ladder/util2'
import {Primary} from "ladder/ux/button2"
import {Icon} from 'ladder/ux/icon2'
import {trackedUrl} from 'ladder/ux/link2'
import {Text} from 'ladder/ux/typography2'

import {preferredCarrierToUwGuidelinesUrl} from "./common2"
import {WeightField, HeightField, SingleSelectOptions} from './inputs2';
import './PreScreener2.css'

let linkIconSvg = "M16.4069 5.42098C14.8173 5.54979 13.3044 6.15868 12.0689 7.16698L12.0419 7.18798H12.0418C11.7069 7.45595 11.5403 7.88223 11.6049 8.30623C11.6695 8.73025 11.9554 9.08768 12.3549 9.24373C12.7544 9.39988 13.2068 9.33093 13.5418 9.06296L13.6048 9.01199C14.3933 8.36706 15.348 7.95836 16.3588 7.83299C17.4081 7.71326 18.4692 7.91297 19.4033 8.40604C20.3373 8.89911 21.1007 9.66259 21.5938 10.5966C22.0869 11.5305 22.2866 12.5917 22.1669 13.6411C22.0435 14.6502 21.6391 15.6044 20.9999 16.3951L20.9489 16.458C20.7643 16.6889 20.6714 16.9797 20.688 17.2747C20.7046 17.5698 20.8296 17.8484 21.0388 18.0571C21.2806 18.3034 21.6166 18.4337 21.9613 18.4148C22.3058 18.3958 22.6256 18.2294 22.8388 17.958L22.8598 17.9311V17.931C23.8681 16.6954 24.4771 15.1825 24.6058 13.593C24.7115 12.1276 24.3926 10.6627 23.6873 9.37375C22.982 8.08487 21.9202 7.02657 20.629 6.3255C19.3379 5.62442 17.872 5.31037 16.407 5.4208L16.4069 5.42098ZM8.99986 13.605L9.05083 13.542C9.2354 13.3112 9.32828 13.0203 9.31168 12.7253C9.29508 12.4303 9.17018 12.1517 8.96091 11.943C8.7195 11.7019 8.38688 11.5748 8.04626 11.5938C7.70563 11.6126 7.38913 11.7757 7.17586 12.042L7.15486 12.069V12.0691C6.14656 13.3046 5.53766 14.8176 5.40886 16.4071C5.30359 17.8706 5.62175 19.3336 6.32546 20.6213C7.02916 21.909 8.08864 22.9669 9.37721 23.6686C10.6659 24.3704 12.1295 24.6864 13.5927 24.579C15.1823 24.4502 16.6952 23.8413 17.9307 22.833L17.9577 22.812H17.9578C18.2927 22.5441 18.4592 22.1178 18.3947 21.6938C18.33 21.2698 18.0442 20.9123 17.6447 20.7563C17.2452 20.6001 16.7927 20.6691 16.4578 20.937L16.3948 20.988C15.6063 21.6329 14.6516 22.0416 13.6408 22.167C12.5915 22.2867 11.5303 22.087 10.5963 21.594C9.6623 21.1009 8.89883 20.3374 8.40575 19.4034C7.91268 18.4695 7.71298 17.4083 7.8327 16.3589C7.95604 15.3498 8.36043 14.3956 8.9997 13.6049L8.99986 13.605ZM12.0509 16.251L16.2508 12.051C16.5542 11.7477 16.9963 11.6293 17.4106 11.7403C17.825 11.8513 18.1486 12.175 18.2597 12.5893C18.3707 13.0037 18.2522 13.4458 17.9489 13.7491L13.7489 17.9491C13.4456 18.2524 13.0035 18.3709 12.5891 18.2598C12.1748 18.1488 11.8511 17.8252 11.7401 17.4108C11.6291 16.9964 11.7475 16.5544 12.0509 16.251Z";
function InfoIcon({ infoIconUrl }) {
    return <a target="_blank" href={trackedUrl({url: infoIconUrl, id: "ladder.agents.dashboard.pre-screener/ladder-for-agents-overview"})}>
        <Icon icon={[ // ladder.svg/svg("agents/quoter/link-icon")
            "agents/quoter/link-icon", {width: "30", height: "30", viewBox: "0 0 30 30", fill: "none"},
            <path fillRule="evenodd" clipRule="evenodd" fill="#8E8E8E" d={linkIconSvg}/>]}
              className="icon" color="medium-gray" no-stroke={true}/></a>;
}

function fitCheckProgressSummary(props) {
    function bmiOutOfRange(height, weight) {
        let bmi = inchesLbsToBmi(height, weight);
        return !(16.5 < bmi && bmi < 46.5);
    }

    // { height, weight, unemploymentDisability, hardDrugs, felony, marijuana, majorMedicalConditions }
    let height = props['height'];
    let weight = props['weight'];
    let unemploymentDisability = props['unemployment-disability'];
    let hardDrugs = props['hard-drugs'];
    let felony = props['felony'];
    let marijuana = props['marijuana'];
    let majorMedicalConditions = props['major-medical-conditions'];

    //console.assert(false, "todo snake case conversion");
    if (height == null || weight == null) return { stopAt: 'height-and-weight' };
    if (bmiOutOfRange(height, weight)) return { outcome: 'bmi-ineligible', stopAt: 'height-and-weight' };

    if (unemploymentDisability == null) return { stopAt: 'unemployment-disability' };
    if (unemploymentDisability) return { outcome: 'not-a-fit', stopAt: 'unemployment-disability' };

    if (hardDrugs == null) return { stopAt: 'hard-drugs' };
    if (hardDrugs) return { outcome: 'not-a-fit', stopAt: 'hard-drugs' };

    if (felony == null) return { stopAt: 'felony' };
    if (felony) return { outcome: 'not-a-fit', stopAt: 'felony' };

    if (marijuana == null) return { stopAt: 'marijuana' };
    if (marijuana) return {
        outcome: majorMedicalConditions ? 'warn-medical-and-marijuana' : 'warn-marijuana-use',
        complete: majorMedicalConditions != null
    };

    if (majorMedicalConditions == null) return { stopAt: 'major-medical-conditions' };
    if (majorMedicalConditions) return { outcome: 'warn-medical-history', complete: true };

    return { outcome: 'no-problems', complete: true };
}

const questionBank = {
    'height-and-weight': { prompt: "Please enter your client’s height and weight:" },
    'unemployment-disability': { prompt: "Is your client currently unemployed and collecting disability or other government assistance benefits?" },
    'hard-drugs': { prompt: "Has your client ever used hard drugs?", infoIcon: true },
    'felony': { prompt: "Does your client have any felony convictions, or felony charges pending?" },
    'marijuana': { prompt: "Does your client use marijuana or cannabis products on a weekly or daily basis?" },
    'major-medical-conditions': { prompt: "Does your client have any major medical conditions?", infoIcon: true }
};

function Question({answers, changeAnswer, questionId, infoIconUrl}) {
    // console.log("Question answers: ", answers); // they are blank wtf
    const { prompt, infoIcon } = questionBank[questionId];
    return <div className="QuestionContainer">
        <div className="question-row">
            <Text size="body-12">{prompt}</Text>
            {infoIcon && <InfoIcon infoIconUrl={infoIconUrl} />}
        </div>
        {questionId === 'height-and-weight' ?
            <div className="height-weight-answer-row">
                <div className="AnswerHeight">
                    <HeightField
                        onChange={(value) => changeAnswer("height", value)}
                        height="m"
                        height50px={true}
                        quoteHeightInches={answers.height}
                        removeBorder={true} />
                </div>
                <div className="AnswerWeight">
                    <WeightField
                        onChange={(value) => changeAnswer("weight", value)}
                        height="m"
                        height50px={true}
                        textSize='13'
                        quoteWeightPounds={answers.weight}
                        removeBorder={true}
                        floatingPlaceholder={false} /></div>
            </div> :
            <div className="AnswerYesOrNo">
                <SingleSelectOptions
                    options={[
                        { "option-value": true, "option-label": "Yes", emoji: "🙆" },
                        { "option-value": false, "option-label": "No", emoji: "🙅" }]}
                    grow={true} value={answers[questionId]}
                    onChange={(value) => changeAnswer(questionId, value)} />
            </div>}
    </div>;
}

function FitMeter({fitRating}) {
    const fitCss = {
        low: 'fitMeterLowFit',
        'low-moderate': 'fitMeterLowModerateFit',
        moderate: 'fitMeterModerateFit',
        good: 'fitMeterGoodFit'
    }[fitRating];

    return <div className="fitMeterContainer">
        <div className={`fitMeterRow ${fitCss}`}>
            {Array.from({ length: 9 }).map((_, i) => (<div key={i} className="fitMeterRect fitMeterRectRed"><div className="fitMeterTriangle"></div></div>))}
            {Array.from({ length: 9 }).map((_, i) => (<div key={i} className="fitMeterRect fitMeterRectYellow"><div className="fitMeterTriangle"></div></div>))}
            {Array.from({ length: 9 }).map((_, i) => (<div key={i} className="fitMeterRect fitMeterRectGreen"><div className="fitMeterTriangle"></div></div>))}
        </div>
        <div className="fitMeterRow">
            <div className="fitMeterLabel fitMeterLabelRed">Low fit</div>
            <div className="fitMeterLabel fitMeterLabelYellow">Moderate fit</div>
            <div className="fitMeterLabel fitMeterLabelGreen">Good fit</div>
        </div>
    </div>;
}

function FitCheckOutcome({ outcome, complete, redirectStartQuote, answers, sendMutation }) {
    let getOutcomeDetails = (outcome: any) => {
        switch (outcome) {
            case 'bmi-ineligible':
                return ['red', false, `Sorry, your client’s calculated BMI of ${inchesLbsToBmi(answers.height, answers.weight)} is outside Ladder’s guidelines`];
            case 'not-a-fit':
                return ['red', false, "Sorry, Ladder isn’t a good fit for your client"];
            case 'warn-medical-history':
                return ['yellow', true, "Depending on severity and other details, Ladder can offer coverage to people with more complicated medical histories. Some cases may be referred to manual underwriting, or may be declined."];
            case 'warn-marijuana-use':
                return ['yellow', true, "Depending on frequency, type, and other health history, Ladder can offer coverage to moderate marijuana users. Some cases may be referred to manual underwriting."];
            case 'warn-medical-and-marijuana':
                return ['yellow', true, "Ladder may be a good fit for your client, but we’ll need to know more about their medical history and marijuana use."];
            case 'no-problems':
                return ['green', true, "So far, so good! Answering a few more questions will tell us more."];
            default:
                return [];
        }
    };

    let [color, canProceedToQuote, copy] = getOutcomeDetails(outcome);
    let terminalState = complete || !canProceedToQuote;
    useEffect(() => { // simple inlined <Beacon>
        if (terminalState) {
            let impressionData = {}; // (get-in flags [:experiment/impression-source flag])
            sendMutation.mutate({def: "analytics/feature-encountered",
                                params: {...impressionData,
                                        "feature-name": canProceedToQuote ? "pre_screener_terminal_state_can_proceed" : "pre_screener_terminal_state_blocked",
                                        flag: null,
                                        "experiment-impression-source-override": null
                                }});
        }
    }, [terminalState]);
    return <div className={`outcome ${color || ''}`}>
        <Text className={`outcome-text ${color || ''}`} desktopSize='body-16' mobileSize='body-12'>{copy}</Text>
        {canProceedToQuote && complete && <div className='button-container'>
            <Primary className="button" fullWidth={true} onClick={() => {
                let monitor = sendMutation.mutate({def: "agent-dash/start-quote-from-pre-screener", params: {}}); // beacon only
                scrollToIdSmooth("ladder-agents-quote-container");
                redirectStartQuote(answers['height'], answers['weight']);}}>
                Start a quote</Primary>
        </div>}</div>;
}

const questionsInOrder = ['height-and-weight', 'unemployment-disability', 'hard-drugs', 'felony', 'marijuana', 'major-medical-conditions'];
const questionIndexes = questionsInOrder.reduce((acc, questionId, index) => { acc[questionId] = index; return acc; }, {});
let outcomeToFitRating = {
    "no-problems": "good",
    "warn-medical-history": "moderate",
    "warn-marijuana-use": "moderate",
    "warn-medical-and-marijuana": "low-moderate",
    "not-a-fit": "low",
    "bmi-ineligible": "low"
};

/**
 * Query: :agent/preferred-carrier
 * sends: ::answer-question (beacon true)
 * mutations: 'agent-dash/set-pre-screener-answer (local, beacon=true)
 * state: :agent-dash/pre-screener-answers
 **/
export function FitCheck2({answerQuestion, redirectStartQuote, preScreenerAnswers}) {
    let sendMutation = useMutation({ mutationFn: (x) => axios  // one mutation at a time for now, not many
            .post('/ui2/mutation', x, {headers: canonicalHeaders()})});

    let read = useQuery({ queryKey: ['PreScreener1'], queryFn: () => axios
            .post('/ui2/agents2/pre-screener1', null, {headers: canonicalHeaders()})
            .then(res => res.data)});

    if (read.isLoading) { return <div>Loading...</div> }
    if (read.error) { return <div>{'An error has occurred: ' + read.error['message']}</div> }
    let preferredCarrier = read.data['agent/preferred-carrier'];
    let progressSummary = fitCheckProgressSummary(preScreenerAnswers);
    const questionsToRender = progressSummary.stopAt ?
        questionsInOrder.slice(0, questionIndexes[progressSummary.stopAt] + 1) :
        questionsInOrder;

    return <div className="QuestionList">
            {questionsToRender.map((questionId) =>
                <Question
                    key={questionId} questionId={questionId} answers={preScreenerAnswers}
                    infoIconUrl={preferredCarrierToUwGuidelinesUrl(preferredCarrier)}
                    changeAnswer={(kstr, v) => {
                        answerQuestion(kstr, v);
                        let params = { id: kstr, [(kstr === "height" || kstr === "weight") ? "value-int" : "value-bool"]: v };
                        // @ts-ignore -- wtf
                        let monitor = sendMutation.mutate({def: "agent-dash/set-pre-screener-answer", params: params});
                    }}/>)}
            {'outcome' in progressSummary &&
                <div className="Results">
                    <FitMeter fitRating={outcomeToFitRating[progressSummary.outcome]}/>
                    <FitCheckOutcome
                        sendMutation={sendMutation} redirectStartQuote={redirectStartQuote}
                        outcome={progressSummary.outcome} complete={progressSummary.complete} answers={preScreenerAnswers} />
                </div>}
        </div>;
}
